import axios from "axios";
import ENUM from '@/enum/lib';
import moment from "moment-timezone";

// Debug helper: wrap localStorage.setItem to trace who overwrites tokens.
// Also add a short-lived guard to prevent an immediate overwrite/clear of a freshly-saved refreshToken.
try {
    const __orig_localStorage_setItem = localStorage.setItem.bind(localStorage);
    localStorage.setItem = function(key, value) {
        try {
            // Only instrument token reads/writes to avoid noisy logs
            if (key === 'refreshToken' || key === 'accessToken') {
                const err = new Error();
                const stack = err.stack || '';
                const stackLines = stack.split('\n').map(s => s.trim());

                // Attempt to extract a webpack-internal caller for quick identification
                let probable = null;
                try {
                    const m = stack.match(/webpack-internal:\/\/\/\.\/([^:\)\n]+):(\d+):(\d+)/);
                    if (m) probable = {file: m[1], line: m[2], col: m[3]};
                } catch (e) {}

                // debug logs removed in cleanup
            }

            // Protection: when refreshToken was just saved, suppress immediate clears/overwrites.
            if (key === 'refreshToken') {
                const isClearing = (value === '' || value === undefined || value === null);
                if (isClearing && window.__justSavedRefreshToken && !window.__forceClearRefreshToken) {
                    // suppressed clearing refreshToken because it was recently saved
                    return; // skip the clear to keep the freshly-saved token
                }

                if (!isClearing) {
                    try {
                        window.__justSavedRefreshToken = true;
                        setTimeout(() => { try { window.__justSavedRefreshToken = false; } catch (e) {} }, 5000);
                    } catch (e) {}
                }
            }
        } catch (e) {}
        // keep in-memory cache in sync for tokens to avoid read-after-write race
        try {
            if (key === 'accessToken') {
                try { window.__tokenCache.accessToken = value; } catch (e) {}
            }
            if (key === 'refreshToken') {
                try { window.__tokenCache.refreshToken = value; } catch (e) {}
            }
        } catch (e) {}
        return __orig_localStorage_setItem(key, value);
    }
} catch (e) {
    // ignore in environments where localStorage is not writable
}

// Wrap removeItem and clear to detect deletes
try {
    const __orig_localStorage_removeItem = localStorage.removeItem.bind(localStorage);
    localStorage.removeItem = function(key) {
        try {
            if (key === 'refreshToken' || key === 'accessToken') {
                const stack = (new Error()).stack || '';
                const stackLines = stack.split('\n').slice(2, 8).map(s => s.trim());
                // removeItem debug removed
            }
        } catch (e) {}
        return __orig_localStorage_removeItem(key);
    }
} catch (e) {}

try {
    const __orig_localStorage_clear = localStorage.clear.bind(localStorage);
    localStorage.clear = function() {
        try {
            const stack = (new Error()).stack || '';
            const stackLines = stack.split('\n').slice(2, 8).map(s => s.trim());
            // clear debug removed
        } catch (e) {}
        return __orig_localStorage_clear();
    }
} catch (e) {}

// Watcher to log refreshToken changes for a short period. Usage: window.startRefreshTokenWatch(5000)
window.startRefreshTokenWatch = function(durationMs = 5000, intervalMs = 100) {
    try {
        const start = Date.now();
        let prev = localStorage.getItem('refreshToken');
    // start refresh token watch
        const id = setInterval(() => {
            try {
                const cur = localStorage.getItem('refreshToken');
                if (cur !== prev) {
                    // refreshToken changed
                    prev = cur;
                }
                if (Date.now() - start > durationMs) {
                    clearInterval(id);
                    // refreshToken watch finished
                }
            } catch (e) {}
        }, intervalMs);
        return id;
    } catch (e) { /* startRefreshTokenWatch failed */ }
}

export const _TIME_OUT = 60 * 1000;
export const _CONTACT_MAIL = 'contact@hroboticsinc.com';
export const _SERVICE_MAIL = 'service@hroboticsinc.com';
export const _API = process.env.VUE_APP_API_DOMAIN;
export const _HEADER = () => {
    return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Method': 'GET, POST, OPTIONS, PUT',
        'Access-Control-Allow-Headers': 'x-access-token,x-refresh-token, x-lang',
        'x-access-token': localStorage.getItem( 'accessToken' ) || '',
        'x-refresh-token': localStorage.getItem( 'refreshToken' ) || '',
        'x-lang': localStorage.getItem( 'lang' ) || _DEFAULT_LANGUAGE,
        'tz' : moment.tz.guess()
    }
};
export const _DEFAULT_LANGUAGE = 'en';

// Ensure axios includes latest tokens from localStorage on every request.
axios.interceptors.request.use(config => {
    try {
        config.headers = config.headers || {};
    // Prefer in-memory token cache when available to avoid transient nulls
    const cache = window.__tokenCache || {};
    const _access = cache.accessToken !== undefined ? cache.accessToken : localStorage.getItem('accessToken');
    if (_access) config.headers['x-access-token'] = _access;
    const _reqRefresh = cache.refreshToken !== undefined ? cache.refreshToken : localStorage.getItem('refreshToken');
    if (_reqRefresh) config.headers['x-refresh-token'] = _reqRefresh;
        config.headers['x-lang'] = localStorage.getItem('lang') || _DEFAULT_LANGUAGE;
    // debug: show tokens used for this request
    // request interceptor debug removed
    return config;
    } catch (e) {
        return config;
    }
}, error => Promise.reject(error));

// Ensure we capture updated tokens from any response headers and persist them.
axios.interceptors.response.use(response => {
    try {
        _ACCESS_TOKEN_SETTING(response);
    // response interceptor debug removed
    } catch (e) {}
    return response;
}, error => {
    try {
        if (error && error.response) _ACCESS_TOKEN_SETTING(error.response);
    // response interceptor error debug removed
    } catch (e) {}
    return Promise.reject(error);
});

/**
 * 클라이언트 스토리지를 비우고, / 으로 리다이렉트 하는 메소드.
 * @name CLEAR_STORAGE_AND_REDIRECT
 * @method
 * @private
 */
export const _CLEAR_STORAGE_AND_REDIRECT = () => {
    try {
        // If debugging flag set on window, skip clearing & redirect so developer can inspect state
        try {
                if (window.__suppressAutoLogout) {
                    return;
                }
        } catch (e) {}
        // Avoid repeatedly calling updateToken if we are already on the login page
        if (window.location.pathname === '/login') {
            // nothing to do
        } else if (!window.__clearSessionInProgress) {
            window.__clearSessionInProgress = true;
            const url = _API + `/user/updateToken`;
            const conf = {
                headers: _HEADER()
            };

            // Only attempt update if there is an access token present
            try {
                const currentAccess = localStorage.getItem('accessToken');
                if (currentAccess) {
                    _AXIOS_HELPER(ENUM.HTTP_METHOD.PUT, url, {fcmToken: ''}, conf)
                        .then(({data}) => {
                            if (data.isSuccess)
                                try { this.$fbConf.REMOVE_ALL_NOTI_ITEMS_FROM_DB(); } catch (e) {}
                        })
                        .catch(() => {
                        })
                        .finally(() => {
                            // release lock after a short delay to avoid duplicates
                            setTimeout(() => { window.__clearSessionInProgress = false; }, 2000);
                        })
                } else {
                    // no access token, nothing to update
                    window.__clearSessionInProgress = false;
                }
            } catch (e) {
                window.__clearSessionInProgress = false;
            }
        }
    } catch (e) {

    }

    try {
        // Respect recent-save guards to avoid races where login just saved tokens
        // and another flow immediately clears them. Use window.__forceClearRefreshToken
        // to override when a true forced clear is needed.
        try {
                if (!window.__justSavedRefreshToken || window.__forceClearRefreshToken) {
                    localStorage.setItem('refreshToken', '');
                }
        } catch (e) {
            // best-effort fallback
            try { localStorage.setItem('refreshToken', ''); } catch (e2) {}
        }

        try {
            if (!window.__justSavedAccessToken) {
                localStorage.setItem('accessToken', '');
            }
        } catch (e) {
            try { localStorage.setItem('accessToken', ''); } catch (e2) {}
        }

        try {
            localStorage.setItem('fcmToken', '');
        } catch (e) {}
    } catch (e) {
        console.debug('[CLEAR] clearing storage failed', e);
    }

    // navigate to login only if not already there
    if (window.location.pathname !== '/login') {
        location.href = '/login'
    }
};

export const _ERROR_CHECK = (err) => {
    try {
        // If debugging flag set on window, suppress automatic logout to allow inspection
        if (window.__suppressAutoLogout) {
            return;
        }
        const language = navigator?.language || navigator?.userLanguage;
        let currentLang = localStorage.getItem('lang') ?? language;
        const korean = currentLang.includes('ko');

        // TODO : 중국어 등 다국어 처리 필요
        const msg = korean ? {
            '400': '잘못된 요청입니다. 새로고침 후 다시 시도해 주세요.',
            '401': '세션이 만료되었습니다. (로그인 페이지로 이동합니다.)',
            '500': '서버에 오류가 발생하여 요청을 수행할 수 없습니다. 새로고침 후 다시 시도해 주세요.',
            'xxx': '서버에 오류가 발생하여 요청을 수행할 수 없습니다. 새로고침 후 다시 시도해 주세요.',
        } : {
            '400': 'Bad request. Please refresh and try again.',
            '401': 'Session has expired. (Go to the login page.)',
            '500': 'The server encountered an error and could not perform the request. Please refresh and try again.',
            'xxx': 'The server encountered an error and could not fulfill the request. Please refresh and try again.',
        };

        const errorCode = err?.response?.status ? err?.response?.status : err?.name;

        let eleDiv = document.createElement('div');
        eleDiv.id = `errorDiv`;

        if (String(errorCode).startsWith('400'))
            eleDiv.innerText = `[${errorCode}] ${msg['400']}`;
        else if (String(errorCode).startsWith('401'))
            eleDiv.innerText = `[${errorCode}] ${msg['401']}`;
        else if (String(errorCode).startsWith('500'))
            eleDiv.innerText = `[${errorCode}] ${msg['500']}`;
        else
            eleDiv.innerText = `[${errorCode}] ${msg['xxx']}`;

        eleDiv.classList.add('full-fill-alert-error10');

        document.body.appendChild(eleDiv);

        setTimeout(function () {
            eleDiv.classList.add('opacity-zero');

            setTimeout(function () {
                if (String(errorCode).startsWith('401'))
                    _CLEAR_STORAGE_AND_REDIRECT();
            }, 2000);
        }, 3000);
    } catch (e) {
    }
}

/**
 * AXIOS헬퍼.
 * @param method http 메소드. ENUM.HTTP_METHOD 참조
 * @param url
 * @param data
 * @param conf
 * @param checkFormData
 * @returns {Promise<unknown>}
 * @public
 */
export const _AXIOS_HELPER = ( method, url, data, conf, checkFormData = false ) => {
    if (!window.location.href.includes('https://manager.rebless.clinic')) {
        try {
            let db;
            const idxedDB = window.indexedDB;
            const request = idxedDB.open('API');
            request.onupgradeneeded = (e) => {
                db = e.target.result;

                if (!db.objectStoreNames.contains('API LIST(DEV)'))
                    db.createObjectStore('API LIST(DEV)');
            }

            request.onsuccess = function (event) {
                db = event.target.result;

                function checkDay() {
                    db.transaction(['API LIST(DEV)']).objectStore('API LIST(DEV)').getAll().onsuccess = function (event) {
                        event.target.result?.map((value) => {
                            if ((value.day + 4) <= Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24)))
                                db.transaction(['API LIST(DEV)'], "readwrite").objectStore('API LIST(DEV)').delete(value.key)
                        })
                    };
                }

                function setData() {
                    let methodStr = '';
                    switch (method) {
                        case ENUM.HTTP_METHOD.GET : {
                            methodStr = 'GET';
                            break;
                        }
                        case ENUM.HTTP_METHOD.POST : {
                            methodStr = 'POST';
                            break;
                        }
                        case ENUM.HTTP_METHOD.PUT : {
                            methodStr = 'PUT';
                            break;
                        }
                        case ENUM.HTTP_METHOD.DELETE : {
                            methodStr = 'DELETE';
                            break;
                        }
                        case ENUM.HTTP_METHOD.PATCH : {
                            methodStr = 'PATCH';
                            break;
                        }
                        default : {
                            methodStr = 'UNKNOWN';
                        }
                    }

                    const transaction = db.transaction(['API LIST(DEV)'], 'readwrite');
                    const store = transaction.objectStore('API LIST(DEV)');
                    store.put({
                        methodStr,
                        url,
                        data,
                        conf,
                        id: localStorage.getItem('devId'),
                        userAgent: navigator.userAgent,
                        key: `${new Date().toString()}:${url}`,
                        day: Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24)),
                    }, `${new Date().toString()}:${url}`);
                }

                checkDay();
                setData();
            };
        } catch (e) {

        }
    }

    return new Promise((resolve, reject) => {
        switch (method) {
            case ENUM.HTTP_METHOD.GET : {
                _AXIOS_GET(url, data, {
                    ...conf,
                    timeout: _TIME_OUT,
                })
                    .then(resolve)
                    .catch(err => {
                        _ERROR_CHECK(err);
                        reject(err);
                    });
                break;
            }
            case ENUM.HTTP_METHOD.POST : {
                _AXIOS_POST(url, data, {
                    ...conf,
                    timeout: _TIME_OUT,
                }, checkFormData)
                    .then(resolve)
                    .catch(err => {
                        _ERROR_CHECK(err);
                        reject(err);
                    });
                break;
            }
            case ENUM.HTTP_METHOD.PUT : {
                _AXIOS_PUT(url, data, {
                    ...conf,
                    timeout: _TIME_OUT,
                })
                    .then(resolve)
                    .catch(err => {
                        _ERROR_CHECK(err);
                        reject(err);
                    });
                break;
            }
            case ENUM.HTTP_METHOD.DELETE : {
                _AXIOS('delete', url, data, {
                    ...conf,
                })
                    .then(resolve)
                    .catch(err => {
                        _ERROR_CHECK(err);
                        reject(err);
                    });
                break;
            }
            case ENUM.HTTP_METHOD.PATCH : {
                _AXIOS('patch', url, data, {
                    ...conf,
                })
                    .then(resolve)
                    .catch(err => {
                        _ERROR_CHECK(err);
                        reject(err);
                    });
                break;
            }
            default : {
                const errorTxt = `[AXIOS_HANDLER]UNKNOWN METHODS`
                _ERROR_CHECK({
                    name: 'error',
                    message: errorTxt,
                });
                reject(errorTxt);
            }
        }
    });
};

/**
 * x-access-token 업데이트
 * @param res
 * @private
 */
function _ACCESS_TOKEN_SETTING (res) {
    if (!res || !res.headers) return;
    if (res.headers['x-access-token'])
        accessTokenUpdater(res.headers['x-access-token']);
    if (res.headers['x-refresh-token'])
        refreshTokenUpdater(res.headers['x-refresh-token']);
}

const refreshTokenUpdater = ( token ) => {
    try {
        const current = localStorage.getItem('refreshToken');
        if (token && token !== current) {
            localStorage.setItem('refreshToken', token);
        }
    } catch (e) {
        // ignore
    }
}

/**
 * axios get 요청 메소드, 아직은 필요가 없지만 url 뒤에 파라메터 필요 시 parameterSetting 를 활용
 * @param url
 * @param data
 * @param conf
 * @returns {Promise<unknown>}
 * @private
 */
const _AXIOS_GET = (  url, data, conf ) => {
    return new Promise((resolve, reject) => {
    // GET request
    axios.get(url, conf)
            .then(res => {
                _ACCESS_TOKEN_SETTING(res);
                resolve(res);
            })
            .catch(err => {
                reject(err);
            })
    });
}

/**
 * axios를 post요청으로 주어진 파라메터에 맞게 설정하여 요청합니다. 굳이 이런 함수를 뜯은 이유는,
 * 요청에는 reponse의 header에 access token이 업데이트 될 수 있기 때문입니다.
 * 따라서 특별한 경우가 아니라면, 해당 메소드로 post 요청을 보내십시오.
 * @name AXIOS_POST
 * @method
 * @param url request URL
 * @param data request body data. its for JSON type as default.
 * @param conf header of request
 * @returns {Promise<unknown>}
 * @private
 */
const _AXIOS_POST = ( url, data, conf, checkFormData ) => {
    return new Promise((resolve, reject) => {
    // POST request
    axios.post(url, checkFormData ? data : JSON.stringify(data), conf)
            .then(res => {
                _ACCESS_TOKEN_SETTING(res);
                resolve(res);
            })
            .catch(err => {
                reject(err);
            })
    });
};

/**
 * axios를 put요청으로 주어진 파라메터에 맞게 설정하여 요청합니다. 굳이 이런 함수를 뜯은 이유는,
 * 요청에는 reponse의 header에 access token이 업데이트 될 수 있기 때문입니다.
 * 따라서 특별한 경우가 아니라면, 해당 메소드로 post 요청을 보내십시오.
 * @name AXIOS_PUT
 * @method
 * @param url request URL
 * @param data request body data. its for JSON type as default.
 * @param conf header of request
 * @returns {Promise<unknown>}
 * @private
 */
const _AXIOS_PUT = ( url, data, conf ) => {
    return new Promise((resolve, reject) => {
    // PUT request
    axios.put(url, JSON.stringify(data), conf)
            .then(res => {
                _ACCESS_TOKEN_SETTING(res);
                resolve(res);
            })
            .catch(err => {
                reject(err);
            })
    });
};

const _AXIOS = ( method, url, data, conf ) => {
    return new Promise((resolve, reject) => {
    // generic request
        axios({
            method: method,
            data: data,
            url: url,
            headers: conf.headers,
            timeout: conf?.timeout === undefined ? _TIME_OUT : conf?.timeout,
        })
            .then(res => {
                _ACCESS_TOKEN_SETTING(res);
                resolve(res);
            })
            .catch(err => {
                reject(err);
            })
    });
};

export const parameterSetting = (params) => {
    try {
        let paramStr = '';

        if (params.length !== 0) {
            for (let key in params)
                paramStr += `${key}=${params[key]}&`;

            paramStr = `?${paramStr.slice(0, -1)}`
        }

        return paramStr;
    } catch (e) {
        return '';
    }
}

const accessTokenUpdater = ( token ) => {
    const currentToken = localStorage.getItem('accessToken');
    if (token !== currentToken) {
        localStorage.setItem('accessToken', token);
    }
}

//객체를 던지면 해당 객체에 key 가 아무것도 없는지, 즉 {} 인지 확인한다.
// 비어있는 객체가 맞으면 true 리턴
export const checkObjectKeyIsEmpty = function ( obj ) {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
}

export const DROP_DECIMAL_POINT = (n, pos) => { // 지정자리 버림 (값, 자리수)
    const digits = Math.pow(10, pos);
    let num = Math.floor(n * digits) / digits;
    return num.toFixed(pos);
}

export const copyStyles = (sourceDoc, targetDoc) => {
    Array.from(sourceDoc.styleSheets).forEach(styleSheet => {
        if (styleSheet.cssRules) {
            // for <style> elements
            const newStyleEl = sourceDoc.createElement("style");

            Array.from(styleSheet.cssRules).forEach(cssRule => {
                // write the text of each rule into the body of the style element
                newStyleEl.appendChild(sourceDoc.createTextNode(cssRule.cssText));
            });

            targetDoc.head.appendChild(newStyleEl);
        } else if (styleSheet.href) {
            // for <link> elements loading CSS from a URL
            const newLinkEl = sourceDoc.createElement("link");

            newLinkEl.rel = "stylesheet";
            newLinkEl.href = styleSheet.href;
            targetDoc.head.appendChild(newLinkEl);
        }
    });
}

export const cookiesList = {
    essential: [
        'essentialCookies',
        'performanceCookies',
        'functionalCookies',
        'fcmToken',
        'refreshToken',
        'accessToken',
        'unit',
        'lang',
        'userEmail',
        'userName',
        'seqUser',
        'seqManager',
        'devId',
        'time',
    ],
    performance: [
        '_ga',
        '_ga_M5TZ02M2MX',
        '_ga_9NMHB72MZS',
    ],
    functional: [
        'popupYN',
        'payPopup'
    ],
}

const expireValues = {
    'unit': 365,
    'lang': 365,
}

export const setCookie = (name, value, expireDays, pass = false) => {
    const essential = getCookie('essentialCookies');
    const performance = getCookie('performanceCookies');
    const functional = getCookie('functionalCookies');

    let checkList = [];
    if (essential === '1')
        checkList = checkList.concat(cookiesList.essential);

    if (performance === '1')
        checkList = checkList.concat(cookiesList.performance);

    if (functional === '1')
        checkList = checkList.concat(cookiesList.functional);

    if (pass || checkList.includes(name)) {
        let expires = expireDays;

        if (!isNaN(expireDays)) {
            const date = new Date();
            date.setDate(date.getDate() + expireDays);
            expires = date.toUTCString();
        }

        expires = expireValues[name] || expires;

        let domain = '';
        if (window.location.href.includes('.rebless.clinic')) {
            domain = 'domain=rebless.clinic;';
        } else if (window.location.href.includes('.amplifyapp.com')) {
            domain = `domain=amplifyapp.com;`;
        }

        document.cookie = escape(name) + "=" + escape(value) + "; expires=" + expires + ";path=/;" + domain;
    }
}

export const getCookie = (name) => {
    const cookie = document.cookie;
    if (cookie !== "") {
        const cookie_array = cookie.split("; ");
        for (let i = 0; cookie_array.length > i; i++) {
            let cookie_name = cookie_array[i].split("=");
            if (cookie_name[0] === name) {
                return unescape(cookie_name[1]);
            }
        }
    }
    return '';
}

export const dataProcess = (data, t) => {
    return data?.exerciseList.map((item, index) => {
        let exercisePart;
        try {
            const tempUsage = item.iuppiterUsageActivePosition.split(' ')
            exercisePart = [
                tempUsage[0] === undefined ? item.exerciseGuideDirection : Number(tempUsage[0]),
                tempUsage[1] === undefined ? item.exerciseGuidePosition : Number(tempUsage[1]),
            ];
        } catch (e) {
            exercisePart = [
                item.exerciseGuideDirection,
                item.exerciseGuidePosition,
            ];
        }

        let tempDate;

        try {
            if (item.usageStartDatetime !== null)
                tempDate = moment(item.usageStartDatetime).locale(t('country')).format(`YYYY/MM/DD HH:mm:ss`)
            else
                tempDate = moment(item.createDatetime).locale(t('country')).format(`YYYY/MM/DD HH:mm:ss`)
        } catch (e) {
            tempDate = moment(item.createDatetime).locale(t('country')).format(`YYYY/MM/DD HH:mm:ss`)
        }

        return {
            ...item,
            process: {
                direction: exercisePart[0],
                position: exercisePart[1],
                date: tempDate,
                exerciseGuideExtension: item.exerciseGuideExtension,
                exerciseGuideFlexion: item.exerciseGuideFlexion,
                exerciseGuideMinRom: item.exerciseGuideMinRom,
                exerciseGuideMaxRom: item.exerciseGuideMaxRom,
                exerciseGuideSpeed: item.exerciseGuideSpeed,
                iuppiterUsageCount: item.iuppiterUsageCount,
                deviceUsageDuration: moment.duration(item.deviceUsageDuration, "second").asSeconds(),
                iuppiterUsageMinSpeed: item.iuppiterUsageMinSpeed,
                iuppiterUsageMaxSpeed: item.iuppiterUsageMaxSpeed,
                iuppiterUsageMode: item.iuppiterUsageMode,
                iuppiterUsageMinAngle: item.iuppiterUsageMinAngle,
                iuppiterUsageMaxAngle: item.iuppiterUsageMaxAngle,
            },
            not: {
                exerciseGuideMode: item.exerciseGuideMode,    // iuppiterUsageMode
                exerciseGuideCount: item.exerciseGuideCount,  // iuppiterUsageCount
                exerciseGuideTime: item.exerciseGuideTime,    // deviceUsageDuration

                iuppiterUsageMinTorque: item.iuppiterUsageMinTorque,  // X
                iuppiterUsageMaxTorque: item.iuppiterUsageMaxTorque,  // X
            },
        }
    })
}

export const excelExport = (fullData, t, fileName = '', allData = false) => {
    try {
        const date = new Date().format('YYYY-MM-DD');
        const Excel = require('exceljs');
        const workbook = new Excel.Workbook();
        workbook.creator = 'h robotics';
        workbook.lastModifiedBy = 'h robotics';
        workbook.created = new Date();
        workbook.modified = new Date();

        const sheetOne = (name, email, fullData, sheetIndex = 1) => {
            try {
                const borderStyle = {
                    top: {style: 'thin'},
                    left: {style: 'thin'},
                    bottom: {style: 'thin'},
                    right: {style: 'thin'}
                };
                const userName = name;
                const userEmail = email;

                // region 시트1 생성
                const workSheetOneName = String(`${allData === false ? '' : `${userEmail.substring(0, 15)}_`}${t('rehabilitation.record')}`).substring(0, 30);
                workbook.addWorksheet(workSheetOneName);
                const sheetOne = workbook.getWorksheet(workSheetOneName);

                // region 시트1 - 제목
                sheetOne.mergeCells(`A1:M1`);
                sheetOne.getCell('A1').value = `${t('device_type.rebless_home')} ${t('patient.rehabilitation_history')}`;
                sheetOne.getCell('A1').font = {size: 14, bold: true};
                sheetOne.getCell('A1').alignment = {horizontal: 'center'};
                // endregion
                // region 시트1 - 내용1
                sheetOne.mergeCells(`A2:M2`);
                sheetOne.getCell('A2').value = `${t('patients.name')} / ${t('patients.email')} : ${userName} /  ${userEmail}`;
                sheetOne.getCell('A2').font = {size: 12, bold: false};
                sheetOne.getCell('A2').alignment = {horizontal: 'right'};
                // endregion
                // region 시트1 - 내용2
                sheetOne.addRow();
                // endregion
                // region 헤더
                const headerData1 = [
                    t('status.date'),
                    t('rehabilitation.exercise_method'),
                    '',
                    '',
                    t('rehabilitation.min_rom'),
                    t('rehabilitation.max_rom'),
                    t('menu.settings'),
                    '',
                    `${t('excel.exerciseTime')}`,
                    t('excel.reps'),
                    t('menu.settings'),
                    '',
                    '',
                ]
                const headerData2 = [
                    '',
                    t('rehabilitation.mode'),
                    t('rehabilitation.position'),
                    t('rehabilitation.direction'),
                    '',
                    '',
                    `${t('rehabilitation.min_rom')}`,
                    `${t('rehabilitation.max_rom')}`,
                    '',
                    '',
                    `${t('rehabilitation.down')}`,
                    `${t('rehabilitation.up')}`,
                    `${t('rehabilitation.speed')}`,
                ];
                sheetOne.addRow(headerData1)
                sheetOne.addRow(headerData2)

                sheetOne.mergeCells(`B4:D4`)
                sheetOne.mergeCells(`G4:H4`)
                sheetOne.mergeCells(`K4:M4`)

                sheetOne.mergeCells(`A4:A5`)
                sheetOne.mergeCells(`E4:E5`)
                sheetOne.mergeCells(`F4:F5`)
                sheetOne.mergeCells(`I4:I5`)
                sheetOne.mergeCells(`J4:J5`)

                for (let loopRow = 4; loopRow <= 5; loopRow++) {
                    for (let loopCol = 1; loopCol <= headerData1.length; loopCol++) {
                        const col = sheetOne.getRow(loopRow).getCell(loopCol);
                        col.border = {
                            ...borderStyle,
                        };
                        col.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: {argb: 'c0c0c0'},
                        };
                        col.alignment = {vertical: 'middle', horizontal: 'center'}
                    }
                }
                // endregion
                try {
                    // region 시트1 데이터
                    fullData
                        .map((value, index) => {
                            const item = value.process
                            const modeText = `${item.iuppiterUsageMode === 0 ? t('exercise.mode.active') : ''}${item.iuppiterUsageMode === 1 ? t('exercise.mode.passive') : ''}${item.iuppiterUsageMode === 2 ? t('exercise.mode.passive_measure') : ''}${item.iuppiterUsageMode === 3 ? t('exercise.mode.active_measure') : ''}`;
                            const directionText = `${item.direction === 1 ? t('exercise.direction.left') : ''}${item.direction === 2 ? t('exercise.direction.right') : ''}`;
                            const positionText = `${item.position === 1 ? t('exercise.position.elbow') : ''}${item.position === 2 ? t('exercise.position.wrist') : ''}${item.position === 3 ? t('exercise.position.knee') : ''}${item.position === 4 ? t('exercise.position.Ankle') : ''}`;
                            const time = isNaN(item.deviceUsageDuration) ? 0 : item.deviceUsageDuration;

                            const row = {
                                date: item.date,
                                mode: modeText,
                                direction: directionText,
                                position: positionText,
                                realMinRom: item.iuppiterUsageMinAngle,
                                realMaxRom: item.iuppiterUsageMaxAngle,
                                minRom: item.exerciseGuideMinRom,
                                maxRom: item.exerciseGuideMaxRom,
                                time: moment.utc(time * 1000).format('HH:mm:ss'),
                                count: item.iuppiterUsageCount,
                                extension: item.exerciseGuideExtension,
                                flexion: item.exerciseGuideFlexion,
                                speed: item.exerciseGuideSpeed,
                            }

                            const tempData = [
                                row.date,
                                row.mode,
                                row.position,
                                row.direction,
                                row.realMinRom,
                                row.realMaxRom,
                                item.iuppiterUsageMode === 0 || item.iuppiterUsageMode === 1 ? row.minRom : '-',
                                item.iuppiterUsageMode === 0 || item.iuppiterUsageMode === 1 ? row.maxRom : '-',
                                row.time,
                                item.iuppiterUsageMode === 0 || item.iuppiterUsageMode === 1 ? row.count : '-',
                                item.iuppiterUsageMode === 0 ? row.extension : '-',
                                item.iuppiterUsageMode === 0 ? row.flexion : '-',
                                item.iuppiterUsageMode === 1 ? row.speed : '-',
                            ];

                            sheetOne.addRow(tempData);

                            if (item.iuppiterUsageMode === 0 || item.iuppiterUsageMode === 1) {
                                if (row.realMinRom !== row.minRom) {
                                    sheetOne.getRow(index + 6).getCell(5).font = {color: {argb: "0000ff"}};
                                    sheetOne.getRow(index + 6).getCell(5).fill = {
                                        type: 'pattern',
                                        pattern: 'solid',
                                        fgColor: {
                                            argb: "ffff00"
                                        }
                                    };
                                    sheetOne.getRow(index + 6).getCell(7).font = {color: {argb: "ff0000"}};
                                    sheetOne.getRow(index + 6).getCell(7).fill = {
                                        type: 'pattern',
                                        pattern: 'solid',
                                        fgColor: {
                                            argb: "ffff00"
                                        }
                                    };
                                }

                                if (row.realMaxRom !== row.maxRom) {
                                    sheetOne.getRow(index + 6).getCell(6).font = {color: {argb: "0000ff"}};
                                    sheetOne.getRow(index + 6).getCell(6).fill = {
                                        type: 'pattern',
                                        pattern: 'solid',
                                        fgColor: {
                                            argb: "ffff00"
                                        }
                                    };
                                    sheetOne.getRow(index + 6).getCell(8).font = {color: {argb: "ff0000"}};
                                    sheetOne.getRow(index + 6).getCell(8).fill = {
                                        type: 'pattern',
                                        pattern: 'solid',
                                        fgColor: {
                                            argb: "ffff00"
                                        }
                                    };
                                }
                            }

                            for (let loop = 1; loop <= headerData1.length; loop++) {
                                const col = sheetOne.getRow(index + 6).getCell(loop);
                                col.border = borderStyle;
                                sheetOne.getColumn(loop).width = loop === 1 ? 30 : 20;

                                if (sheetOne.getRow(index + 6).getCell(loop).value === '-') {
                                    sheetOne.getRow(index + 6).getCell(loop).value = '';
                                    sheetOne.getRow(index + 6).getCell(loop).fill = {
                                        type: 'pattern',
                                        pattern: 'solid',
                                        fgColor: {argb: 'f0f0f0'},
                                    }
                                }
                            }
                        })
                    // endregion
                } catch (e) {
                    console.error('error row A', e)
                }
                // region 필터 / 틀고정
                sheetOne.autoFilter = {
                    from: 'B5',
                    to: 'D5',
                }
                sheetOne.views = [
                    {
                        state: 'frozen',
                        xSplit: 4,
                        ySplit: 5
                    }
                ];
                sheetOne.properties.defaultRowHeight = 20;
                // endregion
                // endregion
            } catch (e) {
                workbook.addWorksheet(`${sheetIndex}_Sheet 1`);
                console.error('error sheet A', e)
            }
        }

        if (fullData.length !== 0) {
            fullData.map((data, index) => {
                const userName = data.userName.replace(/[`~!#$%^&*()_|+\-=?;:'",<>\{\}\[\]\\\/ ]/gim, '');
                const userEmail = data.userEmail.replace(/[`~!#$%^&*()_|+\-=?;:'",<>\{\}\[\]\\\/ ]/gim, '');

                sheetOne(userName, userEmail, data.data, index + 1);
            })
        } else {
            workbook.addWorksheet(`Sheet 1`);
        }

        workbook.xlsx.writeBuffer().then((data) => {
            const blob = new Blob([data], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `${allData === true ? '' : `${fileName}_`}${t('device_type.rebless_home')} ${t('patient.rehabilitation_history')}_${date}.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);
        })
    } catch (error) {
        console.error('error excel B', error)
    }
}

// In-memory token cache to avoid race between localStorage writes and axios request interceptor
try {
    window.__tokenCache = {
        accessToken: localStorage.getItem('accessToken'),
        refreshToken: localStorage.getItem('refreshToken'),
    };
    // During development, default to suppress automatic logout so developers can inspect state.
    // Can be overridden by setting VUE_APP_ENABLE_SESSION_TIMEOUT=true in .env
    try {
        if (typeof window.__suppressAutoLogout === 'undefined') {
            const enableSessionTimeout = process.env.VUE_APP_ENABLE_SESSION_TIMEOUT === 'true';
            const isProduction = process.env.NODE_ENV === 'production';
            
            if (!isProduction && !enableSessionTimeout) {
                window.__suppressAutoLogout = true;
            }
        }
    } catch (e) {}
} catch (e) {
    try { window.__tokenCache = { accessToken: null, refreshToken: null }; } catch (e2) {}
}